import React, { useEffect, useState, useCallback } from 'react';
import { AiOutlineBell } from 'react-icons/ai';
import {
    canSeePurchasingNotifications,
    dismissPurchasingNotification,
    fetchPurchasingNotifications,
    PURCHASING_NOTIFICATIONS_CHANGED,
} from '../services/purchasingNotificationsService';

// PURCHNOTIF-2026-10-31 — campana del menú superior: avisos de cambio de costo en Purchase Orders.
// Azul = pending, gris = dismissed. Punto rojo mientras haya alguno pending.
const POLL_MS = 60000;

const styles = {
    header: { padding: '0.6rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', borderBottom: '1px solid #E2E8F0', color: '#0f172a' },
    item: (pending) => ({
        background: pending ? '#E7F1FF' : '#F1F3F5',
        color: pending ? '#0f172a' : '#64748B',
        padding: '0.55rem 0.9rem',
        borderBottom: '1px solid #E2E8F0',
        fontSize: '0.8rem',
    }),
    dot: { top: 8, right: 4, width: 10, height: 10 },
};

export const PurchasingNotificationBell = () => {
    const [data, setData] = useState({ pending: [], recent: [], pendingCount: 0 });
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            setData(await fetchPurchasingNotifications());
        } catch (error) {
            console.log('PurchasingNotificationBell:', error.message);
        }
    }, []);

    useEffect(() => {
        if (!canSeePurchasingNotifications()) return undefined;
        load();
        const interval = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, POLL_MS);
        window.addEventListener(PURCHASING_NOTIFICATIONS_CHANGED, load);
        return () => {
            clearInterval(interval);
            window.removeEventListener(PURCHASING_NOTIFICATIONS_CHANGED, load);
        };
    }, [load]);

    if (!canSeePurchasingNotifications()) return null;

    const dismiss = async (id) => {
        setBusyId(id);
        try {
            await dismissPurchasingNotification(id);
        } catch (error) {
            console.log('PurchasingNotificationBell:', error.message);
        }
        setBusyId(null);
    };

    const renderItem = (n, pending) => (
        <li key={n.id} style={styles.item(pending)}>
            <div className="pn-item-row d-flex justify-content-between align-items-start gap-2">
                <div className="pn-item-text">
                    <div>{n.text}</div>
                    <small>{new Date(n.created_at).toLocaleString()}{!pending && n.resolved_by ? ` · seen by ${n.resolved_by}` : ''}</small>
                </div>
                {pending && (
                    <button type="button" className="btn btn-sm text-nowrap pn-seen-btn" disabled={busyId === n.id} onClick={() => dismiss(n.id)}>
                        Mark as seen
                    </button>
                )}
            </div>
        </li>
    );

    return (
        <div className="nav-item dropdown pn-bell">
            <button
                type="button"
                className="btn text-light position-relative"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded="false"
                aria-label="Purchasing notifications"
            >
                <AiOutlineBell className="h4 mb-0" />
                {data.pendingCount > 0 && (
                    <span className="position-absolute bg-danger border border-light rounded-circle" style={styles.dot}>
                        <span className="visually-hidden">Pending notifications</span>
                    </span>
                )}
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow pn-menu">
                <li style={styles.header}>Cost changes ({data.pendingCount} pending)</li>
                {data.pending.length === 0 && data.recent.length === 0 && (
                    <li style={styles.item(false)}>No notifications</li>
                )}
                {data.pending.map((n) => renderItem(n, true))}
                {data.recent.map((n) => renderItem(n, false))}
            </ul>
        </div>
    );
};
